import { isRouteError, RouteError } from '@/utils/RouteError';
import { nextResponseForRouteError, responseBodyForRouteError } from '@/utils/nextResponseForRouteError';
import { logger as baseLogger } from '@/logger';
import type { Logger } from 'pino';
import { trace } from '@opentelemetry/api';
import { ZodError } from 'zod';
import { NextResponse } from 'next/server';
import { IRouteErrorResponse } from '@/interfaces/IRouteErrorResponse';

interface IServerErrorParams {
  sourceException?: Error | RouteError;
  name?: string;
  message?: string;
  statusCode?: number;
  shouldLog?: boolean;
  shouldWriteTelemetry?: boolean;
  logger?: Logger;
}

interface SpecifiedServerErrorParams {
  sourceException?: Error | RouteError;
  name: string;
  message: string;
  statusCode: number;
  shouldLog: boolean;
  shouldWriteTelemetry: boolean;
  logger: Logger;
}

export class ServerError {
  sourceException?: Error | RouteError;
  name: string;
  message: string;
  statusCode: number;
  shouldLog: boolean;
  shouldWriteTelemetry: boolean;
  logger: Logger;

  static addDefaultToParams(params: IServerErrorParams): SpecifiedServerErrorParams {
    return {
      shouldLog: true,
      shouldWriteTelemetry: true,
      message: 'Unknown error',
      statusCode: 500,
      logger: baseLogger,
      name: 'Error',
      ...params,
    };
  }

  static paramsFromError(
    error: unknown,
  ): Omit<IServerErrorParams, 'logger' | 'shouldWriteTelemetry' | 'shouldLog'> {
    let sourceError = error;
    let message = 'Unknown error';
    let name = 'Error';
    let statusCode = 500;

    if (isRouteError(error)) {
      sourceError = error;
      message = error.message;
      statusCode = error.status;
      name = error.name;
    } else if (error instanceof ZodError) {
      const routeError = RouteError.fromZodError(error);
      sourceError = routeError;
      message = routeError.message;
      statusCode = 400;
      name = 'ValidationError';
    } else if (error instanceof Error) {
      sourceError = error;
      message = error.message;
    }

    return {
      sourceException: sourceError as Error,
      statusCode,
      message,
      name,
    };
  }

  constructor(params: IServerErrorParams) {
    const paramsWithDefaults = ServerError.addDefaultToParams(params);

    this.name = paramsWithDefaults.name;
    this.sourceException = paramsWithDefaults.sourceException;
    this.message = paramsWithDefaults.message;
    this.statusCode = paramsWithDefaults.statusCode;
    this.shouldLog = paramsWithDefaults.shouldLog;
    this.shouldWriteTelemetry = paramsWithDefaults.shouldWriteTelemetry;
    this.logger = paramsWithDefaults.logger;
  }

  static fromError(error: unknown, logger: Logger = baseLogger): ServerError {
    let params = ServerError.paramsFromError(error);

    let shouldLog = !params.statusCode || params.statusCode < 400 || params.statusCode >= 500;
    return new ServerError({
      ...params,
      shouldLog: shouldLog,
      shouldWriteTelemetry: shouldLog,
      logger,
    });
  }

  static fromMessage(message: string, logger: Logger = baseLogger): ServerError {
    return new ServerError({
      message,
      logger,
    });
  }

  withName(name: string) {
    this.name = name;
    return this;
  }

  withMessage(message: string) {
    this.message = message;
    return this;
  }

  withStatusCode(statusCode: number) {
    this.statusCode = statusCode;
    return this;
  }

  withoutLogging() {
    this.shouldLog = false;
    return this;
  }

  withLogging() {
    this.shouldLog = true;
    return this;
  }

  withoutAzureException() {
    this.shouldWriteTelemetry = false;
    return this;
  }

  withAzureException() {
    this.shouldWriteTelemetry = true;
    return this;
  }

  withLogger(logger: Logger) {
    this.logger = logger;
    return this;
  }

  /**
   * Will handle the error and return a NextResponse to use if you want to return a response
   */
  handle(): NextResponse<IRouteErrorResponse> {
    this.logIfNeeded();
    const routeError = this.buildRouteError();
    if (!routeError) {
      // Bit of a failsafe in case we couldn't build a route error for some reason
      return NextResponse.json({
        error: this.message,
        name: this.name,
        isRouteErrorResponse: true,
      });
    }
    return nextResponseForRouteError(routeError);
  }

  logAndReturnRouteError(): IRouteErrorResponse {
    this.logIfNeeded();
    const routeError = this.buildRouteError();
    if (!routeError) {
      // Bit of a failsafe in case we couldn't build a route error for some reason
      return {
        error: this.message,
        name: this.name,
        isRouteErrorResponse: true,
      };
    }
    return responseBodyForRouteError(routeError);
  }

  /**
   * Indicates that the source error is a RouteError, and the status code and message are the same as before, so
   * we can re-use it and preserve the original stack trace
   * @private
   */
  private canReuseRouteError() {
    return (
      !!this.sourceException &&
      isRouteError(this.sourceException) &&
      this.sourceException.message === this.message &&
      (this.sourceException.status === this.statusCode || this.statusCode === undefined)
    );
  }

  private buildRouteError() {
    if (this.canReuseRouteError() && isRouteError(this.sourceException)) {
      return this.sourceException;
    }

    try {
      return new RouteError(
        {
          name: this.name,
          error: this.message,
          fieldErrors: isRouteError(this.sourceException) ? this.sourceException.fieldErrors : undefined,
        },
        this.statusCode,
      );
    } catch (e) {
      // Absolute failsafe - we can't really ever have a failure here. If we do, we'll just return a generic error
      console.error('Could not build route error', e, this.message);
    }
  }

  private sanitizeStack(stack: string) {
    return stack
      .split('\n')
      .filter(line => !line.includes('ServerError')) // This is always going to be stuff that is not relevant
      .join('\n');
  }

  private writeTelemetry() {
    // If we have a source error, and the source error's message is not the same as the message we're going to log,
    // we'll include the source error's message in the telemetry message
    const sourceExceptionHasMessage = !!this.sourceException?.message;
    const messageIsDifferent = this.message !== this.sourceException?.message;
    const messageToPost =
      sourceExceptionHasMessage && messageIsDifferent
        ? `${this.message}: ${this.sourceException?.message}`
        : this.message;
    const stack = this.sanitizeStack(this.sourceException?.stack || '');

    trace.getActiveSpan()?.recordException({
      name: this.name || 'Error',
      message: messageToPost,
      stack: stack,
      code: this.name || 'Error',
    });
  }

  private writeLog() {
    if (this.sourceException) {
      this.logger.error(this.sourceException, this.message);
    } else {
      // We have no source exception. Let's make one!
      const routeError = this.buildRouteError();
      this.logger.error(routeError, this.message);
    }
  }

  private logIfNeeded() {
    if (this.shouldWriteTelemetry) {
      try {
        this.writeTelemetry();
      } catch {
        console.warn('Could not add exception to telemetry', this.message);
      }
    }

    if (this.shouldLog) {
      try {
        this.writeLog();
      } catch (e) {
        // We don't want to throw an error if we can't log the error
        console.error('Could not log error', e, this.message);
      }
    }
  }

  logAndThrowOriginalError(): never {
    this.logIfNeeded();
    if (this.sourceException) {
      throw this.sourceException;
    }
    throw this.buildRouteError();
  }
  logAndThrowRouteError(): never {
    this.logIfNeeded();
    throw this.buildRouteError();
  }
}

export class ServerErrorLogger {
  logger: Logger;
  defaultName?: string;

  constructor(logger: Logger, defaultName?: string) {
    this.logger = logger;
    this.defaultName = defaultName;
  }

  fromError(error: unknown): ServerError {
    const serverError = ServerError.fromError(error).withLogger(this.logger);

    if (serverError.name === 'Error' && this.defaultName) {
      serverError.withName(this.defaultName);
    }

    return serverError;
  }

  fromMessage(message: string): ServerError {
    const error = ServerError.fromMessage(message).withLogger(this.logger);

    if (this.defaultName) {
      error.withName(this.defaultName);
    }

    return error;
  }

  static forLogger(logger: Logger, defaultName?: string) {
    return new ServerErrorLogger(logger, defaultName);
  }

  static forService(service: string, parentLogger: Logger = baseLogger) {
    return new ServerErrorLogger(parentLogger.child({ service }), `${service}Error`);
  }
}
